import { useState, useCallback, useEffect, useRef } from 'react';
import { GameProvider, useGameState, useGameDispatch } from './GameProvider';
import { ThemeProvider } from './theme/ThemeProvider';
import { useFirebaseSync } from './hooks/useFirebaseSync';
import { setHand, clearRoom, setError } from './gameActions';
import gameService from './gameService';
import { storageKey } from '../storageKeys';
import GameEntry from './phases/GameEntry';
import PokerTable from './components/PokerTable';
import DealerControls from './components/DealerControls';
import ArrangingScreen from './phases/ArrangingScreen';
import ComparisonModal from './components/ComparisonModal';
import RulesViewer from './components/RulesViewer';
import ShowdownScreen from './phases/ShowdownScreen';
import ResultsScreen from './phases/ResultsScreen';
import DealerSuccession from './components/DealerSuccession';
import ErrorBoundary from './components/ErrorBoundary';
import './theme/tokens.css';

function GameInner({ avatars, onExitToMenu, onScoreTracker, pwaInstall }) {
  const state = useGameState();
  const dispatch = useGameDispatch();
  const { gameId, uid, phase, dealerUid } = state;

  const [showRules, setShowRules] = useState(false);
  const [comparisonTarget, setComparisonTarget] = useState(null);

  // Firebase sync
  useFirebaseSync(gameId, uid, dispatch);

  // Landscape lock
  useEffect(() => {
    if (window.screen?.orientation?.lock) {
      window.screen.orientation.lock('landscape').catch(() => {
        // Silently fail — not all browsers support this
      });
    }
    return () => {
      if (window.screen?.orientation?.unlock) {
        window.screen.orientation.unlock();
      }
    };
  }, []);

  const isDealer = uid === dealerUid;

  // Dealer soft-disconnect presence — only the CURRENT dealer keeps a handler.
  // A former dealer cancels its stale handler here, which fixes dealerUid
  // flapping / the false "you are now the dealer" popup after a transfer.
  useEffect(() => {
    if (!gameId || !uid) return undefined;
    if (uid === dealerUid) {
      gameService.armDealerPresence(gameId);
      return () => { gameService.disarmDealerPresence(gameId); };
    }
    gameService.disarmDealerPresence(gameId);
    return undefined;
  }, [gameId, uid, dealerUid]);

  // Handlers
  const handleEnterGame = useCallback((gid, userId) => {
    // Game entered — Firebase sync will populate state
  }, []);

  const handleDeal = useCallback(async () => {
    if (!isDealer || !gameId) return;
    try {
      await gameService.dealCards(gameId);
    } catch (err) {
      dispatch(setError(err.message));
    }
  }, [isDealer, gameId, dispatch]);

  const handleReady = useCallback(async () => {
    try {
      console.log('[SHOWDOWN][handleReady] submitting arrangement + setting ready | isDealer:', uid === dealerUid);
      await gameService.submitArrangement(gameId, uid, state.arrangement);
      await gameService.setReady(gameId, uid, true, 'arranging');
      console.log('[SHOWDOWN][handleReady] ready written to Firebase');
      // Showdown is triggered by the useEffect that watches state.readyStatus.
      // No direct triggerShowdown calls here — the useEffect checks allReady properly.
    } catch (err) {
      console.error('[SHOWDOWN][handleReady] FAILED:', err.message);
      dispatch(setError(err.message));
    }
  }, [gameId, uid, dealerUid, state.arrangement, dispatch]);

  const handleUnready = useCallback(async () => {
    await gameService.setReady(gameId, uid, false);
  }, [gameId, uid]);

  const handleBetweenRoundsReady = useCallback(async (ready) => {
    try {
      await gameService.setBetweenRoundsReady(gameId, uid, ready);
    } catch (err) {
      console.error('setBetweenRoundsReady failed:', err.message);
      dispatch(setError(err.message));
    }
  }, [gameId, uid, dispatch]);

  const handleNemConfirm = useCallback(async (discardedIds, discardedCards) => {
    try {
      console.log('[NEM] Confirming nem:', { discardedIds, discardedCards: discardedCards?.length });
      const result = await gameService.performNem(gameId, uid, discardedIds);
      console.log('[NEM] performNem result:', result);
      if (result.success && result.newCards) {
        dispatch({ type: 'NEM_DISCARD', discardedIds, discardedCards: discardedCards || [], newCards: result.newCards });
        // BUG-1 fix: skip client-side timer reset when CF handled it server-side
        if (!result.usedCloudFunction) {
          await gameService.checkAndResetTimerAfterNem(gameId);
        }
      } else {
        console.error('[NEM] performNem failed or no newCards:', result);
      }
    } catch (err) {
      console.error('[NEM] Error:', err);
      dispatch(setError(err.message));
    }
  }, [gameId, uid, dispatch]);

  const handleAutoNem = useCallback(async () => {
    // Only dealer handles auto-nem for ALL players (avoids race conditions)
    if (!isDealer) return;
    try {
      const nemSnap = await gameService._getNemCompleted(gameId);
      const activeUids = Object.entries(state.players)
        .filter(([, p]) => !p.sittingOut && !p.kicked)
        .map(([id]) => id);
      for (const pUid of activeUids) {
        if (!nemSnap[pUid]) {
          const result = await gameService.autoNem(gameId, pUid);
          // Update local state for the dealer's own hand
          if (pUid === uid && result.success) {
            dispatch({
              type: 'NEM_DISCARD',
              discardedIds: result.discardedCard ? [result.discardedCard.id] : [],
              discardedCards: result.discardedCard ? [result.discardedCard] : [],
              newCards: result.newCard ? [result.newCard] : [],
            });
          }
        }
      }
      await gameService.checkAndResetTimerAfterNem(gameId);
    } catch (err) {
      console.warn('dealer autoNem all failed:', err.message);
    }
  }, [gameId, uid, isDealer, state.players, dispatch]);

  // BUG-10 fix: non-dealers auto-nem themselves when timer expires
  const handleAutoNemSelf = useCallback(async () => {
    try {
      const result = await gameService.autoNem(gameId, uid);
      if (result.success) {
        dispatch({
          type: 'NEM_DISCARD',
          discardedIds: result.discardedCard ? [result.discardedCard.id] : [],
          discardedCards: result.discardedCard ? [result.discardedCard] : [],
          newCards: result.newCard ? [result.newCard] : [],
        });
      }
    } catch (err) {
      console.warn('autoNemSelf failed:', err.message);
    }
  }, [gameId, uid, dispatch]);

  const handleAutoFillAndReady = useCallback(async () => {
    // Auto-fill remaining staging cards into empty row slots, then submit + ready
    const { bottom, middle, top } = state.arrangement;
    const placedIds = new Set([...bottom, ...middle, ...top].map(c => c.id));
    const unplaced = state.myHand.filter(c => !placedIds.has(c.id));

    // Fill empty slots: bottom first, then middle, then top
    const filled = {
      bottom: [...bottom],
      middle: [...middle],
      top: [...top],
    };
    let idx = 0;
    while (filled.bottom.length < 5 && idx < unplaced.length) filled.bottom.push(unplaced[idx++]);
    while (filled.middle.length < 5 && idx < unplaced.length) filled.middle.push(unplaced[idx++]);
    while (filled.top.length < 3 && idx < unplaced.length) filled.top.push(unplaced[idx++]);

    try {
      await gameService.submitArrangement(gameId, uid, filled);
      await gameService.setReady(gameId, uid, true, 'arranging');
      // If dealer, trigger showdown directly after a short delay
      if (isDealer) {
        setTimeout(async () => {
          try {
            await gameService.triggerShowdown(gameId);
          } catch (e) {
            console.warn('Auto showdown failed:', e.message);
          }
        }, 2000);
      }
    } catch (err) {
      dispatch(setError(err.message));
    }
  }, [gameId, uid, state.arrangement, state.myHand, isDealer, dispatch]);

  const handleSortHand = useCallback((sorted) => {
    dispatch(setHand(sorted));
  }, [dispatch]);

  const handleLeaveRoom = useCallback(async () => {
    try {
      await gameService.leaveGame(gameId, uid);
    } catch (err) {
      console.error('leaveGame failed:', err.message);
    }
    dispatch(clearRoom());
    onExitToMenu?.();
  }, [gameId, uid, dispatch, onExitToMenu]);

  const handleSitOut = useCallback(async () => {
    try {
      if (state.players[uid]?.sitOutQueued) {
        await gameService.cancelSitOutQueue(gameId, uid);
      } else {
        await gameService.toggleSitOut(gameId, uid, phase);
      }
    } catch (err) {
      console.error('toggleSitOut failed:', err.message);
    }
  }, [gameId, uid, phase, state.players]);

  const handleTransferDealer = useCallback(async (targetUid) => {
    try {
      await gameService.promoteDealer(gameId, targetUid);
    } catch (err) {
      console.error('transferDealer failed:', err.message);
    }
  }, [gameId]);

  const handleKickPlayer = useCallback(async (targetUid) => {
    if (!isDealer) return;
    try {
      await gameService.kickPlayer(gameId, uid, targetUid);
    } catch (err) {
      console.error('kickPlayer failed:', err.message);
      dispatch(setError(err.message));
    }
  }, [gameId, uid, isDealer, dispatch]);

  const handleUnkickPlayer = useCallback(async (targetUid) => {
    if (!isDealer) return;
    try {
      await gameService.unkickPlayer(gameId, uid, targetUid);
    } catch (err) {
      console.error('unkickPlayer failed:', err.message);
      dispatch(setError(err.message));
    }
  }, [gameId, uid, isDealer, dispatch]);

  // If the dealer kicked me, drop the session and fall back to the entry screen
  const [kickedNotice, setKickedNotice] = useState(false);
  const amKicked = !!state.players[uid]?.kicked;
  useEffect(() => {
    if (amKicked && gameId) {
      setKickedNotice(true);
      localStorage.removeItem(storageKey('xapxam-session'));
      dispatch(clearRoom());
    }
  }, [amKicked, gameId, dispatch]);
  useEffect(() => {
    if (!kickedNotice) return undefined;
    const t = setTimeout(() => setKickedNotice(false), 8000);
    return () => clearTimeout(t);
  }, [kickedNotice]);

  // BUG-6 fix: ref-based guard prevents double showdown trigger
  const showdownTriggeredRef = useRef(false);

  // Reset guard when entering arranging phase
  useEffect(() => {
    if (phase === 'arranging') {
      showdownTriggeredRef.current = false;
    }
  }, [phase]);

  // Check for all ready → trigger showdown (dealer only)
  useEffect(() => {
    if (!isDealer || phase !== 'arranging') return;
    if (showdownTriggeredRef.current) return;

    const activeUids = Object.entries(state.players)
      .filter(([, p]) => !p.sittingOut && !p.kicked)
      .map(([id]) => id);
    if (activeUids.length < 2) return;

    // All active players must be ready in Firebase readyStatus
    const allReady = activeUids.every(id => state.readyStatus[id]);
    console.log('[SHOWDOWN] useEffect check | allReady:', allReady,
      '| readyStatus:', JSON.stringify(state.readyStatus),
      '| activeUids:', activeUids);

    if (allReady) {
      console.log('[SHOWDOWN] All ready! Triggering showdown...');
      showdownTriggeredRef.current = true;
      gameService.triggerShowdown(gameId).then(() => {
        console.log('[SHOWDOWN] triggerShowdown succeeded');
      }).catch(err => {
        console.error('[SHOWDOWN] Failed:', err.message);
        showdownTriggeredRef.current = false; // Allow retry
        dispatch(setError('Showdown failed: ' + err.message));
      });
    }
  }, [isDealer, phase, state.readyStatus, state.players, gameId, dispatch]);

  // If no game yet, show entry screen
  if (!gameId) {
    return (
      <>
        {kickedNotice && (
          <div className="gpt-toast" data-testid="kicked-notice" onClick={() => setKickedNotice(false)}>
            You were removed by the dealer
          </div>
        )}
        <GameEntry
          avatars={avatars}
          onEnterGame={handleEnterGame}
          onExitToMenu={onExitToMenu}
          onScoreTracker={onScoreTracker}
          pwaInstall={pwaInstall}
        />
      </>
    );
  }

  return (
    <>
      <PokerTable
        onPlayerTap={setComparisonTarget}
        onShowRules={() => setShowRules(true)}
        onLeaveRoom={handleLeaveRoom}
        onSitOut={handleSitOut}
        onTransferDealer={handleTransferDealer}
        onKickPlayer={handleKickPlayer}
        onUnkickPlayer={handleUnkickPlayer}
      >
        {/* Center of table content */}
        <DealerControls
          onReady={async (ready) => {
            // First hand (lobby) uses readyStatus; subsequent (results) uses betweenRoundsReady
            if (state.phase === 'lobby') {
              await gameService.setReady(gameId, uid, ready, 'lobby');
            } else {
              await handleBetweenRoundsReady(ready);
            }
          }}
          onDeal={handleDeal}
          onBetweenRoundsReady={handleBetweenRoundsReady}
        />
      </PokerTable>

      {/* Arranging overlay — only for active players (not sitting out) */}
      {phase === 'arranging' && !state.players[uid]?.sittingOut && (
        <ErrorBoundary>
          <ArrangingScreen
            onReady={handleReady}
            onUnready={handleUnready}
            onNemConfirm={handleNemConfirm}
            onAutoNem={handleAutoNem}
            onAutoNemSelf={handleAutoNemSelf}
            onAutoFillAndReady={handleAutoFillAndReady}
            onShowRules={() => setShowRules(true)}
            onSortHand={handleSortHand}
          />
        </ErrorBoundary>
      )}

      {/* Showdown phase */}
      {phase === 'showdown' && <ShowdownScreen />}

      {/* Results phase */}
      {phase === 'results' && <ResultsScreen />}

      {/* Dealer succession (when dealer disconnects) */}
      <DealerSuccession />

      {/* Modals */}
      {showRules && <RulesViewer onClose={() => setShowRules(false)} />}
      {comparisonTarget && (
        <ComparisonModal targetUid={comparisonTarget} onClose={() => setComparisonTarget(null)} />
      )}
    </>
  );
}

// Only force landscape when inside an active game room (not on the entry/lobby screen)
function LandscapeWrapper({ children }) {
  const state = useGameState();
  const inGame = !!state.gameId; // gameId is set once inside a room

  const [isPortrait, setIsPortrait] = useState(
    () => window.innerHeight > window.innerWidth
  );

  useEffect(() => {
    const check = () => setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  // Only force landscape during the arranging phase — the table/lobby/results
  // now use a scrollable layout that works in both orientations.
  const shouldRotate = inGame && isPortrait && state.phase === 'arranging';

  return (
    <div className={shouldRotate ? 'force-landscape' : ''}>
      {children}
    </div>
  );
}

export default function GameShell({ avatars, onExitToMenu, onScoreTracker, pwaInstall }) {
  return (
    <ThemeProvider>
      <GameProvider>
        <LandscapeWrapper>
          <GameInner
            avatars={avatars}
            onExitToMenu={onExitToMenu}
            onScoreTracker={onScoreTracker}
            pwaInstall={pwaInstall}
          />
        </LandscapeWrapper>
      </GameProvider>
    </ThemeProvider>
  );
}
